import React, { useCallback, useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { NavBarLateral } from "../NavBarLateral";
import cuentaUsuariosService from "../../../_services/cart/cuentaUsuarios.service";
import alertService from "../../../_services/cart/alert.service";
import { RolesCuenta } from "./RolesCuenta";
import { ConfirmarAccion } from "./ConfirmarAccion";

/**
 * Mi cuenta > Usuarios: los colaboradores de la cuenta del vendedor y sus roles.
 *
 * Portada de tienda-admin (`pages/mi-estudio-usuarios`, 13b5a5b) manteniendo los mismos pasos,
 * campos, textos y validaciones. Quedan afuera los bloques de esa pantalla que en el marketplace no
 * existen (uso de IA, marketing y almacenamiento) y la solapa "Pagos y facturación", que llega con
 * las suscripciones.
 */

const PAGE_SIZE = 10;

const formatDate = (date) => {
    if (!date) return "—";
    return new Date(date).toLocaleDateString("es-AR", { day: "2-digit", month: "2-digit", year: "numeric" });
};

const initials = (u) => {
    const f = u.firstName?.[0] ?? "";
    const l = u.lastName?.[0] ?? "";
    const init = (f + l).toUpperCase();
    if (init) return init;
    return (u.email?.[0] ?? "?").toUpperCase();
};

const mensajeDe = (err, porDefecto) => err?.message || porDefecto;

/** Input de contraseña con el ojito para mostrarla, como en tienda-admin. */
const CampoPassword = ({ value, onChange, placeholder, visible, onToggle }) => (
    <div className="cuenta-usuarios__password">
        <input
            type={visible ? "text" : "password"}
            value={value}
            placeholder={placeholder}
            autoComplete="new-password"
            onChange={(e) => onChange(e.target.value)}
        />
        {onToggle && (
            <button type="button" onClick={onToggle} title={visible ? "Ocultar" : "Mostrar"}>
                <FontAwesomeIcon icon={visible ? "fa-solid fa-eye-slash" : "fa-solid fa-eye"} />
            </button>
        )}
    </div>
);

export const UsuariosCuenta = () => {
    const [info, setInfo] = useState(null);
    const [isLoadingInfo, setIsLoadingInfo] = useState(true);

    const [usuarios, setUsuarios] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [searchValue, setSearchValue] = useState("");
    const [busqueda, setBusqueda] = useState("");
    const [page, setPage] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [totalElements, setTotalElements] = useState(0);

    const [rolesDisponibles, setRolesDisponibles] = useState([]);
    // Sube cada vez que cambia quien tiene que rol: la seccion de roles muestra cuantos usuarios tiene cada uno.
    const [versionRoles, setVersionRoles] = useState(0);

    // Modal crear usuario
    const [showAddModal, setShowAddModal] = useState(false);
    const [nuevo, setNuevo] = useState({});
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [createError, setCreateError] = useState("");
    const [isSaving, setIsSaving] = useState(false);

    // Modal restablecer contraseña
    const [resetUser, setResetUser] = useState(null);
    const [resetPassword, setResetPassword] = useState("");
    const [resetConfirm, setResetConfirm] = useState("");
    const [showResetPassword, setShowResetPassword] = useState(false);
    const [resetError, setResetError] = useState("");
    const [resetSaving, setResetSaving] = useState(false);

    // Confirmacion de quitar usuario
    const [aQuitar, setAQuitar] = useState(null);

    const cargarRoles = useCallback(() => {
        cuentaUsuariosService
            .roles()
            .then((r) => setRolesDisponibles(r.data || []))
            .catch(() => setRolesDisponibles([]));
        setVersionRoles((v) => v + 1);
    }, []);

    const loadInfo = useCallback(() => {
        cuentaUsuariosService
            .info()
            .then((r) => setInfo(r.data))
            .catch((err) => alertService.error(err))
            .finally(() => setIsLoadingInfo(false));
    }, []);

    const cargar = useCallback(() => {
        setIsLoading(true);
        cuentaUsuariosService
            .buscar({ page, pageSize: PAGE_SIZE, searchValue: busqueda })
            .then((r) => {
                setUsuarios(r.content || []);
                setTotalPages(r.totalPages || 0);
                setTotalElements(r.totalElements || 0);
            })
            .catch((err) => {
                setUsuarios([]);
                alertService.error(err);
            })
            .finally(() => setIsLoading(false));
    }, [page, busqueda]);

    useEffect(() => {
        loadInfo();
        cargarRoles();
    }, [loadInfo, cargarRoles]);

    useEffect(() => {
        cargar();
    }, [cargar]);

    /** Recarga todo lo que cambia al sumar o quitar a alguien (cabecera, lista y conteo por rol). */
    const refrescar = () => {
        loadInfo();
        cargar();
        cargarRoles();
    };

    // --- Busqueda y paginacion ---

    const buscar = () => {
        setPage(0);
        setBusqueda(searchValue);
    };

    const limpiarBusqueda = () => {
        setSearchValue("");
        setPage(0);
        setBusqueda("");
    };

    const pageNumbers = () => {
        const result = [];
        const total = Math.max(totalPages, 1);
        const start = Math.max(0, page - 2);
        const end = Math.min(total, start + 5);
        for (let i = start; i < end; i++) result.push(i);
        return result;
    };

    const usuariosUsados = info?.currentUsers ?? 0;
    const usuariosMax = info?.maxUsers ?? 0;
    const alcanzoLimite = usuariosMax > 0 && usuariosUsados >= usuariosMax;

    // --- Crear usuario ---

    const abrirModalAgregar = () => {
        if (alcanzoLimite) {
            alertService.msg(`Llegaste al límite de ${usuariosMax} usuarios. Cambiá tu plan para sumar más.`, "warning");
            return;
        }
        // Por defecto el rol no-admin (si existe), si no el primero disponible.
        const noAdmin = rolesDisponibles.find((r) => !r.esAdmin);
        setNuevo({
            firstName: "",
            lastName: "",
            email: "",
            password: "",
            confirmPassword: "",
            rolCuentaId: (noAdmin ?? rolesDisponibles[0])?.id ?? null,
        });
        setShowNewPassword(false);
        setShowConfirmPassword(false);
        setCreateError("");
        setShowAddModal(true);
    };

    const cerrarModal = () => {
        setShowAddModal(false);
        setNuevo({});
        setCreateError("");
        setIsSaving(false);
    };

    const agregar = () => {
        setCreateError("");
        if (!nuevo.email?.trim() || !nuevo.firstName?.trim() || !nuevo.lastName?.trim() || !nuevo.password) {
            setCreateError("Completá todos los campos requeridos");
            return;
        }
        if (nuevo.password.length < 8) {
            setCreateError("La contraseña debe tener al menos 8 caracteres");
            return;
        }
        if (nuevo.password !== nuevo.confirmPassword) {
            setCreateError("Las contraseñas no coinciden");
            return;
        }
        if (nuevo.rolCuentaId == null) {
            setCreateError("Seleccioná un rol");
            return;
        }
        setIsSaving(true);
        cuentaUsuariosService
            .crearUsuario({
                email: nuevo.email.trim(),
                password: nuevo.password,
                firstName: nuevo.firstName.trim(),
                lastName: nuevo.lastName.trim(),
                rolCuentaId: nuevo.rolCuentaId,
            })
            .then(() => {
                alertService.msg("Usuario creado y agregado a la cuenta", "success");
                cerrarModal();
                refrescar();
            })
            .catch((err) => {
                setCreateError(mensajeDe(err, "No se pudo crear el usuario"));
                setIsSaving(false);
            });
    };

    // --- Acciones de cada fila ---

    const cambiarRol = (u, nuevoRolId) => {
        if (u.esDuenio || nuevoRolId == null || u.rolCuentaId === nuevoRolId) return;
        cuentaUsuariosService
            .cambiarRol(u.relId, nuevoRolId)
            .then(() => {
                setUsuarios((lista) =>
                    lista.map((it) =>
                        it.relId === u.relId
                            ? { ...it, rolCuentaId: nuevoRolId, rolCuentaNombre: rolesDisponibles.find((r) => r.id === nuevoRolId)?.nombre }
                            : it
                    )
                );
                cargarRoles();
                alertService.msg("Rol actualizado", "success");
            })
            .catch((err) => alertService.msg(mensajeDe(err, "No se pudo cambiar el rol"), "error"));
    };

    const eliminar = (u) => {
        if (u.esDuenio) return;
        setAQuitar(u);
    };

    const confirmarEliminar = () => {
        const u = aQuitar;
        setAQuitar(null);
        cuentaUsuariosService
            .eliminar(u.relId)
            .then(() => {
                alertService.msg("Usuario removido de la cuenta", "success");
                refrescar();
            })
            .catch((err) => alertService.msg(mensajeDe(err, "No se pudo remover el usuario"), "error"));
    };

    // --- Reseteo de contraseña ---

    const abrirResetPassword = (u) => {
        if (u.esDuenio) return;
        setResetUser(u);
        setResetPassword("");
        setResetConfirm("");
        setResetError("");
        setShowResetPassword(false);
    };

    const cerrarResetModal = () => {
        setResetUser(null);
        setResetPassword("");
        setResetConfirm("");
        setResetError("");
        setResetSaving(false);
    };

    const confirmarReset = () => {
        setResetError("");
        if (!resetUser) return;
        if (resetPassword.length < 8) {
            setResetError("La contraseña debe tener al menos 8 caracteres");
            return;
        }
        if (resetPassword !== resetConfirm) {
            setResetError("Las contraseñas no coinciden");
            return;
        }
        setResetSaving(true);
        cuentaUsuariosService
            .resetPassword(resetUser.relId, resetPassword)
            .then(() => {
                alertService.msg("Contraseña actualizada", "success");
                cerrarResetModal();
            })
            .catch((err) => {
                setResetError(mensajeDe(err, "No se pudo actualizar la contraseña"));
                setResetSaving(false);
            });
    };

    // --- Render ---

    const rolPill = (u) => (
        <span className={u.esDuenio ? "cuenta-usuarios__rol cuenta-usuarios__rol--admin" : "cuenta-usuarios__rol"}>
            {u.esDuenio ? "Administrador" : u.rolCuentaNombre || "—"}
        </span>
    );

    const selectorRol = (u) => (
        <select
            className="cuenta-usuarios__select"
            value={u.rolCuentaId ?? ""}
            title="Cambiar rol"
            onChange={(e) => cambiarRol(u, Number(e.target.value))}
        >
            {/* Sin esto un usuario sin rol mostraria el primero de la lista como si lo tuviera. */}
            {u.rolCuentaId == null && (
                <option value="" disabled>
                    Sin rol
                </option>
            )}
            {rolesDisponibles.map((r) => (
                <option key={r.id} value={r.id}>
                    {r.nombre}
                </option>
            ))}
        </select>
    );

    const botonesFila = (u) => (
        <>
            <button type="button" className="cuenta-usuarios__icono" title="Restablecer contraseña" onClick={() => abrirResetPassword(u)}>
                <FontAwesomeIcon icon="fa-solid fa-key" />
            </button>
            <button
                type="button"
                className="cuenta-usuarios__icono cuenta-usuarios__icono--peligro"
                title="Quitar de la cuenta"
                onClick={() => eliminar(u)}
            >
                <FontAwesomeIcon icon="fa-solid fa-user-minus" />
            </button>
        </>
    );

    const nombreUsuario = (u) => (
        <div className="cuenta-usuarios__persona">
            <div className="cuenta-usuarios__avatar">{initials(u)}</div>
            <div className="cuenta-usuarios__persona-datos">
                <p className="cuenta-usuarios__nombre">
                    <span>{u.fullName || u.email}</span>
                    {u.esDuenio && <span className="cuenta-usuarios__dueno">Dueño</span>}
                </p>
                {u.username && (
                    <p className="cuenta-usuarios__username">
                        <FontAwesomeIcon icon="fa-solid fa-id-badge" />
                        {u.username}
                    </p>
                )}
                {u.activo === false && <p className="cuenta-usuarios__inactivo">Cuenta inactiva</p>}
            </div>
        </div>
    );

    return (
        <div className="dashboard">
            <NavBarLateral handleClose={() => {}} />

            <div className="dashboard__content cuenta-usuarios">
                {isLoadingInfo && <p className="panel__estado-carga">Cargando…</p>}

                {!isLoadingInfo && (
                    <>
                        <div className="dashboard__content-header cuenta-usuarios__header">
                            <div className="dashboard__content-title">
                                <p className="cuenta-usuarios__eyebrow">MI CUENTA</p>
                                <h2>Usuarios</h2>
                                <p className="panel__bajada">
                                    Sumá colaboradores, asigná roles y gestioná los accesos a{" "}
                                    <strong>{info?.cuentaNombre || "tu cuenta"}</strong>.
                                </p>
                            </div>

                            <button
                                type="button"
                                className="button"
                                data-type="accent"
                                disabled={alcanzoLimite}
                                onClick={abrirModalAgregar}
                            >
                                <FontAwesomeIcon icon="fa-solid fa-user-plus" />
                                Agregar usuario
                            </button>
                        </div>

                        {/* Buscador */}
                        <section className="cuenta-usuarios__card">
                            <label className="cuenta-usuarios__label">Buscar por nombre, apellido o email</label>
                            <div className="cuenta-usuarios__buscador">
                                <input
                                    type="text"
                                    value={searchValue}
                                    placeholder="Ej: maria@negocio.com..."
                                    onChange={(e) => setSearchValue(e.target.value)}
                                    onKeyUp={(e) => e.key === "Enter" && buscar()}
                                />
                                <button type="button" className="button" data-type="primary" onClick={buscar}>
                                    <FontAwesomeIcon icon="fa-solid fa-magnifying-glass" />
                                    Buscar
                                </button>
                                <button type="button" className="cuenta-usuarios__icono" title="Limpiar" onClick={limpiarBusqueda}>
                                    <FontAwesomeIcon icon="fa-solid fa-xmark" />
                                </button>
                            </div>
                        </section>

                        {/* Tabla */}
                        <section className="cuenta-usuarios__card cuenta-usuarios__tabla-card">
                            {/* Vista mobile: tarjetas */}
                            <div className="cuenta-usuarios__mobile">
                                {isLoading && <p className="panel__estado-carga">Cargando usuarios...</p>}
                                {!isLoading && usuarios.length === 0 && (
                                    <p className="cuenta-usuarios__vacio">Todavía no hay usuarios para mostrar.</p>
                                )}
                                {!isLoading &&
                                    usuarios.map((u) => (
                                        <div key={u.relId} className="cuenta-usuarios__fila-mobile">
                                            <div className="cuenta-usuarios__fila-mobile-head">
                                                {nombreUsuario(u)}
                                                {rolPill(u)}
                                            </div>
                                            <div className="cuenta-usuarios__fila-mobile-datos">
                                                <span><FontAwesomeIcon icon="fa-solid fa-envelope" /> {u.email}</span>
                                                <span><FontAwesomeIcon icon="fa-solid fa-phone" /> {u.numeroCelular || "—"}</span>
                                                <span><FontAwesomeIcon icon="fa-solid fa-calendar" /> {formatDate(u.createdDate)}</span>
                                            </div>
                                            {!u.esDuenio && (
                                                <div className="cuenta-usuarios__acciones">
                                                    {selectorRol(u)}
                                                    {botonesFila(u)}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                            </div>

                            {/* Vista desktop: tabla */}
                            <div className="cuenta-usuarios__desktop">
                                <table className="cuenta-usuarios__tabla">
                                    <thead>
                                        <tr>
                                            <th>Usuario</th>
                                            <th>Email</th>
                                            <th>Teléfono</th>
                                            <th>Rol</th>
                                            <th>Alta</th>
                                            <th className="cuenta-usuarios__centro">Acciones</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {isLoading && (
                                            <tr>
                                                <td colSpan={6} className="cuenta-usuarios__centro">Cargando usuarios...</td>
                                            </tr>
                                        )}
                                        {!isLoading && usuarios.length === 0 && (
                                            <tr>
                                                <td colSpan={6} className="cuenta-usuarios__centro">
                                                    Todavía no hay usuarios para mostrar.
                                                </td>
                                            </tr>
                                        )}
                                        {!isLoading &&
                                            usuarios.map((u) => (
                                                <tr key={u.relId}>
                                                    <td>{nombreUsuario(u)}</td>
                                                    <td>{u.email}</td>
                                                    <td>{u.numeroCelular || "—"}</td>
                                                    <td>{rolPill(u)}</td>
                                                    <td>{formatDate(u.createdDate)}</td>
                                                    <td>
                                                        <div className="cuenta-usuarios__acciones cuenta-usuarios__acciones--centro">
                                                            {u.esDuenio ? (
                                                                <em className="cuenta-usuarios__sin-acciones">Sin acciones</em>
                                                            ) : (
                                                                <>
                                                                    {selectorRol(u)}
                                                                    {botonesFila(u)}
                                                                </>
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                    </tbody>
                                </table>
                            </div>

                            {/* Paginacion */}
                            {totalElements > 0 && (
                                <div className="cuenta-usuarios__paginacion">
                                    <button
                                        type="button"
                                        disabled={page === 0 || isLoading}
                                        onClick={() => page > 0 && setPage(page - 1)}
                                    >
                                        <FontAwesomeIcon icon="fa-solid fa-chevron-left" />
                                    </button>
                                    {pageNumbers().map((p) => (
                                        <button
                                            key={p}
                                            type="button"
                                            className={page === p ? "cuenta-usuarios__pagina--activa" : ""}
                                            onClick={() => setPage(p)}
                                        >
                                            {p + 1}
                                        </button>
                                    ))}
                                    <button
                                        type="button"
                                        disabled={page >= totalPages - 1 || isLoading}
                                        onClick={() => page < totalPages - 1 && setPage(page + 1)}
                                    >
                                        <FontAwesomeIcon icon="fa-solid fa-chevron-right" />
                                    </button>
                                    <span>
                                        Página {page + 1} de {totalPages || 1} ({totalElements}{" "}
                                        {totalElements === 1 ? "usuario" : "usuarios"})
                                    </span>
                                </div>
                            )}
                        </section>

                        {/* Roles de la cuenta */}
                        <RolesCuenta onCambio={cargarRoles} version={versionRoles} />
                    </>
                )}
            </div>

            {/* Modal crear usuario */}
            {showAddModal && (
                <div className="cuenta-usuarios__overlay" onClick={cerrarModal}>
                    <div className="cuenta-usuarios__modal" onClick={(e) => e.stopPropagation()}>
                        <div className="cuenta-usuarios__modal-head">
                            <div>
                                <p className="cuenta-usuarios__eyebrow">MI CUENTA</p>
                                <h3>Crear usuario</h3>
                            </div>
                            <button type="button" className="cuenta-usuarios__cerrar" onClick={cerrarModal}>
                                <FontAwesomeIcon icon="fa-solid fa-xmark" />
                            </button>
                        </div>

                        <div className="cuenta-usuarios__modal-body">
                            <div className="cuenta-usuarios__dos-columnas">
                                <div>
                                    <label className="cuenta-usuarios__label">Nombre</label>
                                    <input
                                        type="text"
                                        placeholder="Juan"
                                        value={nuevo.firstName || ""}
                                        onChange={(e) => setNuevo({ ...nuevo, firstName: e.target.value })}
                                    />
                                </div>
                                <div>
                                    <label className="cuenta-usuarios__label">Apellido</label>
                                    <input
                                        type="text"
                                        placeholder="Pérez"
                                        value={nuevo.lastName || ""}
                                        onChange={(e) => setNuevo({ ...nuevo, lastName: e.target.value })}
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="cuenta-usuarios__label">Email</label>
                                <input
                                    type="email"
                                    placeholder="usuario@ejemplo.com"
                                    value={nuevo.email || ""}
                                    onChange={(e) => setNuevo({ ...nuevo, email: e.target.value })}
                                />
                            </div>

                            <div className="cuenta-usuarios__dos-columnas">
                                <div>
                                    <label className="cuenta-usuarios__label">Contraseña</label>
                                    <CampoPassword
                                        value={nuevo.password || ""}
                                        placeholder="Mínimo 8 caracteres"
                                        visible={showNewPassword}
                                        onToggle={() => setShowNewPassword(!showNewPassword)}
                                        onChange={(v) => setNuevo({ ...nuevo, password: v })}
                                    />
                                </div>
                                <div>
                                    <label className="cuenta-usuarios__label">Confirmar contraseña</label>
                                    <CampoPassword
                                        value={nuevo.confirmPassword || ""}
                                        placeholder="Repetir contraseña"
                                        visible={showConfirmPassword}
                                        onToggle={() => setShowConfirmPassword(!showConfirmPassword)}
                                        onChange={(v) => setNuevo({ ...nuevo, confirmPassword: v })}
                                    />
                                </div>
                            </div>

                            {/* Selector de rol */}
                            <div>
                                <label className="cuenta-usuarios__label">Rol</label>
                                <select
                                    value={nuevo.rolCuentaId ?? ""}
                                    onChange={(e) => setNuevo({ ...nuevo, rolCuentaId: e.target.value ? Number(e.target.value) : null })}
                                >
                                    <option value="" disabled>
                                        Seleccioná un rol…
                                    </option>
                                    {rolesDisponibles.map((r) => (
                                        <option key={r.id} value={r.id}>
                                            {r.nombre}
                                        </option>
                                    ))}
                                </select>
                                <p className="cuenta-usuarios__ayuda">
                                    Los permisos de cada rol se configuran más abajo, en “Roles de la cuenta”.
                                </p>
                            </div>

                            {createError && <div className="cuenta-usuarios__error">{createError}</div>}
                        </div>

                        <div className="cuenta-usuarios__modal-foot">
                            <button type="button" className="button" data-type="secondary" onClick={cerrarModal}>
                                Cancelar
                            </button>
                            <button type="button" className="button" data-type="accent" disabled={isSaving} onClick={agregar}>
                                <FontAwesomeIcon icon={isSaving ? "fa-solid fa-spinner" : "fa-solid fa-user-plus"} spin={isSaving} />
                                {isSaving ? "Creando..." : "Crear usuario"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal restablecer contraseña */}
            {resetUser && (
                <div className="cuenta-usuarios__overlay" onClick={cerrarResetModal}>
                    <div className="cuenta-usuarios__modal cuenta-usuarios__modal--chico" onClick={(e) => e.stopPropagation()}>
                        <div className="cuenta-usuarios__modal-head">
                            <div>
                                <p className="cuenta-usuarios__eyebrow">MI CUENTA</p>
                                <h3>Restablecer contraseña</h3>
                            </div>
                            <button type="button" className="cuenta-usuarios__cerrar" onClick={cerrarResetModal}>
                                <FontAwesomeIcon icon="fa-solid fa-xmark" />
                            </button>
                        </div>

                        <div className="cuenta-usuarios__modal-body">
                            <p className="cuenta-usuarios__texto">
                                Definí una nueva contraseña para{" "}
                                <strong>{resetUser.fullName || resetUser.username || resetUser.email}</strong>. El usuario
                                podrá ingresar con ella de inmediato.
                            </p>

                            <div>
                                <label className="cuenta-usuarios__label">Nueva contraseña</label>
                                <CampoPassword
                                    value={resetPassword}
                                    placeholder="Mínimo 8 caracteres"
                                    visible={showResetPassword}
                                    onToggle={() => setShowResetPassword(!showResetPassword)}
                                    onChange={setResetPassword}
                                />
                            </div>

                            <div>
                                <label className="cuenta-usuarios__label">Confirmar contraseña</label>
                                <CampoPassword
                                    value={resetConfirm}
                                    placeholder="Repetir contraseña"
                                    visible={showResetPassword}
                                    onChange={setResetConfirm}
                                />
                            </div>

                            {resetError && <div className="cuenta-usuarios__error">{resetError}</div>}
                        </div>

                        <div className="cuenta-usuarios__modal-foot">
                            <button type="button" className="button" data-type="secondary" onClick={cerrarResetModal}>
                                Cancelar
                            </button>
                            <button type="button" className="button" data-type="accent" disabled={resetSaving} onClick={confirmarReset}>
                                <FontAwesomeIcon icon={resetSaving ? "fa-solid fa-spinner" : "fa-solid fa-key"} spin={resetSaving} />
                                {resetSaving ? "Guardando..." : "Restablecer"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {aQuitar && (
                <ConfirmarAccion
                    mensaje={`¿Quitar a ${aQuitar.fullName ?? aQuitar.email} de la cuenta?`}
                    confirmLabel="Quitar"
                    onConfirmar={confirmarEliminar}
                    onCancelar={() => setAQuitar(null)}
                />
            )}
        </div>
    );
};
